import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, Center, Text } from '@mantine/core';
import { useGetOrderById } from '../hooks/orders/queries';
import { useGetStoreById } from '../hooks/stores/queries';
import { generateHalfLetterTicketHTMLString } from '../utils/ticketHelper';
import { useTranslation } from 'react-i18next';

type RouteParams = {
  id: string;
};

const OrderPrintHalfLetter = () => {
  const { t } = useTranslation(['FieldName']);
  const { id } = useParams<RouteParams>();
  const { data: order, isLoading: orderLoading, error: orderError } = useGetOrderById(id, {
    fetchSection: 'true'
  });

  const storeId = useMemo(() => {
    if (!order) return '';
    // createdIn puede ser objeto o string
    return typeof order.createdIn === 'string' ? order.createdIn : order.createdIn?._id;
  }, [order]);

  const storeSettings = useMemo(() => {
    if (!order) return undefined;
    return typeof order.createdIn === 'string' ? undefined : order.createdIn;
  }, [order]);

  const { data: store, isLoading: storeLoading } = useGetStoreById(
    !!storeId && !storeSettings,
    storeId
  );

  const isLoading = orderLoading || storeLoading;
  const resolvedStoreSettings = storeSettings || store;

  useEffect(() => {
    if (!order || !resolvedStoreSettings) return;

    (async () => {
      const ticketHtml = await generateHalfLetterTicketHTMLString(
        resolvedStoreSettings,
        order,
        resolvedStoreSettings?.logoUrl,
        t,
        'ticketStructure',
        resolvedStoreSettings?.company?.companyTaxInfo || (resolvedStoreSettings as any)?.companyTaxInfo
      );
      // Reemplaza el documento completo con solo el ticket
      document.open();
      document.write(ticketHtml);
      document.close();
      // Dispara impresión una vez que la función del ticket esté lista
      if (typeof (window as any).printInsider === 'function') {
        await (window as any).printInsider();
      } else {
        window.print();
      }
    })();
  }, [order, resolvedStoreSettings, t]);

  if (orderError) {
    return (
      <Center h="100vh">
        <Text>Error loading order</Text>
      </Center>
    );
  }

  if (isLoading || !order || (!resolvedStoreSettings && storeId)) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  // El ticket se escribe directamente en el documento; no se renderiza JSX adicional
  return null;
};

export default OrderPrintHalfLetter;
